import { useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

/** The highlight behind the pressed button of a segmented control. It is
 *  measured from that button and moved with a transform, so switching glides
 *  instead of jumping; the buttons stay the labelled, clickable elements.
 *  It glides only when the selection changes (a click). Every other
 *  re-measure — a count changing width while typing in search, the minute
 *  refresh, fonts loading, the row wrapping — snaps into place. */
export function SelectionPill({
  container,
  selected,
  rounded = "rounded-xl",
}: {
  /** The row of buttons as an element (a ref callback into state), not a
   *  ref object: a child's layout effect runs before its parent's ref is
   *  attached, so on mount a ref would still be empty. */
  container: HTMLElement | null;
  selected: string;
  rounded?: string;
}) {
  const [pill, setPill] = useState<{
    x: number;
    y: number;
    w: number;
    h: number;
    glide: boolean;
  } | null>(null);
  const lastSelected = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!container) return;
    const read = () => {
      const el = container.querySelector<HTMLElement>('[aria-pressed="true"]');
      return el
        ? { x: el.offsetLeft, y: el.offsetTop, w: el.offsetWidth, h: el.offsetHeight }
        : null;
    };
    // The first placement doesn't glide; a changed selection does.
    const glide = lastSelected.current !== null && lastSelected.current !== selected;
    lastSelected.current = selected;
    const box = read();
    setPill(box ? { ...box, glide } : null);
    // Watch the row and each button in it: in a wrapped row a tab can change
    // width without the row resizing. An unchanged box keeps the current
    // state, so the observer's first callback can't cut a glide short.
    const observer = new ResizeObserver(() => {
      const next = read();
      setPill((prev) =>
        prev &&
        next &&
        prev.x === next.x &&
        prev.y === next.y &&
        prev.w === next.w &&
        prev.h === next.h
          ? prev
          : next
            ? { ...next, glide: false }
            : null,
      );
    });
    observer.observe(container);
    container.querySelectorAll<HTMLElement>("[aria-pressed]").forEach((b) => observer.observe(b));
    return () => observer.disconnect();
  }, [container, selected]);
  if (!pill) return null;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute left-0 top-0 bg-foreground/[0.09] shadow-[inset_0_1px_0_0_hsl(0_0%_100%/0.06)]",
        rounded,
        pill.glide &&
          "transition-[transform,width] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none",
      )}
      style={{
        transform: `translate3d(${pill.x}px, ${pill.y}px, 0)`,
        width: pill.w,
        height: pill.h,
      }}
    />
  );
}
